import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AlertCircle, Check, ChevronDown, Search, X } from "lucide-react";
import MarketingShell from "../MarketingShell";
import SEOHead from "../../components/SEOHead";
import useStoreProducts from "../store/useStoreProducts";
import {
  CATEGORY_GROUPS,
  DEFAULT_SORT,
  JEWELLERY_TYPES,
  METAL_TYPES,
  OPAL_TYPES,
  SORTS,
  categoryGroup,
  jewelleryType,
  metalType,
  opalType,
  sortProducts,
} from "../store/storeTaxonomy";
import { ShopProductCard, SkeletonGrid, StoreMaintenance } from "../store/ShopParts";
import "./shop-catalogue.css";

/**
 * /online-store/shop — the product grid (light-gallery redesign).
 * All published products are loaded once and filtered in the browser, so every
 * filter option shows a live count. Filters are OR within a group and AND
 * across groups, and live in the URL: ?category=&opal=&type=&metal=&search=&sort=
 * (comma-separated values). A `category` value that isn't one of the two
 * groups is matched against real category names, so old links like
 * ?category=Movies keep working.
 */

const PAGE_SIZE = 24;

const GROUPS = [
  { key: "category", label: "Category", options: CATEGORY_GROUPS },
  { key: "opal", label: "Opal type", options: OPAL_TYPES, suffix: " opal" },
  { key: "type", label: "Jewellery", options: JEWELLERY_TYPES },
  { key: "metal", label: "Metal", options: METAL_TYPES },
];

const readList = (params, key) =>
  (params.get(key) || "")
    .split(",")
    .map((v) => v.trim())
    .filter(Boolean);

const inCategory = (product, value) =>
  CATEGORY_GROUPS.includes(value)
    ? categoryGroup(product) === value
    : (product.categories || []).some((c) => (c.name || "").toLowerCase() === value.toLowerCase());

// Whether a product matches one option of a filter group
function inGroup(key, product, value) {
  if (key === "category") return inCategory(product, value);
  if (key === "opal") return opalType(product) === value;
  if (key === "metal") return metalType(product) === value;
  return jewelleryType(product) === value;
}

function passes(product, filters, search, skip) {
  if (skip !== "category" && filters.category.length && !filters.category.some((v) => inCategory(product, v))) return false;
  if (skip !== "opal" && filters.opal.length && !filters.opal.includes(opalType(product))) return false;
  if (skip !== "type" && filters.type.length && !filters.type.includes(jewelleryType(product))) return false;
  if (skip !== "metal" && filters.metal.length && !filters.metal.includes(metalType(product))) return false;
  const q = search.trim().toLowerCase();
  if (!q) return true;
  const haystack = [
    product.name,
    product.sku,
    opalType(product) && `${opalType(product)} opal`,
    jewelleryType(product),
    ...(product.categories || []).map((c) => c.name),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return q.split(/\s+/).every((word) => haystack.includes(word));
}

function useIsMobile() {
  const query = "(max-width: 759px)";
  const [mobile, setMobile] = useState(() => typeof window !== "undefined" && window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = () => setMobile(mq.matches);
    mq.addEventListener ? mq.addEventListener("change", onChange) : mq.addListener(onChange);
    return () => (mq.removeEventListener ? mq.removeEventListener("change", onChange) : mq.removeListener(onChange));
  }, []);
  return mobile;
}

function CheckBox({ on, big }) {
  return (
    <span className={"sc-box" + (on ? " is-on" : "") + (big ? " sc-box--big" : "")}>
      {on && <Check size={big ? 14 : 12} strokeWidth={3} />}
    </span>
  );
}

export default function RShopCataloguePage() {
  const { products, loading, error, maintenance } = useStoreProducts();
  const [params, setParams] = useSearchParams();
  const [menu, setMenu] = useState(null); // 'category' | 'opal' | 'type' | 'sort' | null
  const [shown, setShown] = useState(PAGE_SIZE);
  const mobile = useIsMobile();

  const filters = useMemo(
    () => ({
      category: readList(params, "category"),
      opal: readList(params, "opal"),
      type: readList(params, "type"),
      metal: readList(params, "metal"),
    }),
    [params]
  );
  const search = params.get("search") || "";
  const sort = SORTS.some(([k]) => k === params.get("sort")) ? params.get("sort") : DEFAULT_SORT;

  const update = (patch) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => {
      const value = Array.isArray(v) ? v.join(",") : v;
      if (value) next.set(k, value);
      else next.delete(k);
    });
    setParams(next, { replace: true });
  };

  const toggle = (key, value) => {
    const list = filters[key];
    update({ [key]: list.includes(value) ? list.filter((v) => v !== value) : [...list, value] });
  };

  const clearAll = () => {
    setParams(sort !== DEFAULT_SORT ? { sort } : {}, { replace: true });
    setMenu(null);
  };

  // Back to the first page of results whenever the filters change
  const filterKey = params.toString();
  useEffect(() => setShown(PAGE_SIZE), [filterKey]);

  useEffect(() => {
    if (!menu) return undefined;
    const onKey = (e) => e.key === "Escape" && setMenu(null);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [menu]);

  const results = useMemo(
    () => sortProducts(products.filter((p) => passes(p, filters, search)), sort),
    [products, filters, search, sort]
  );

  // Options with live counts. Opal type and jewellery options with no products
  // at all are hidden (e.g. rings or black opal before any are listed).
  const groups = useMemo(
    () =>
      GROUPS.map((g) => {
        const options = g.options
          .map((value) => ({
            value,
            label: value + (g.suffix || ""),
            count: products.filter((p) => passes(p, filters, search, g.key) && inGroup(g.key, p, value)).length,
            exists: products.some((p) =>
              inGroup(g.key, p, value)
            ),
          }))
          .filter((o) => o.exists || filters[g.key].includes(o.value));
        // Old-style category links (?category=Movies) show as their own option
        if (g.key === "category") {
          filters.category
            .filter((v) => !CATEGORY_GROUPS.includes(v))
            .forEach((v) =>
              options.push({
                value: v,
                label: v,
                count: products.filter((p) => passes(p, filters, search, "category") && inCategory(p, v)).length,
              })
            );
        }
        return { ...g, options };
      }),
    [products, filters, search]
  );

  const chips = [];
  GROUPS.forEach((g) =>
    filters[g.key].forEach((v) => chips.push({ key: `${g.key}:${v}`, label: v + (g.key === "opal" ? " opal" : ""), remove: () => toggle(g.key, v) }))
  );
  if (search.trim()) chips.push({ key: "search", label: `“${search.trim()}”`, remove: () => update({ search: "" }) });

  const anyFilter = filters.category.length || filters.opal.length || filters.type.length || filters.metal.length;
  const title =
    filters.opal.length === 1 && !filters.type.length && !filters.category.length
      ? `${filters.opal[0]} opal`
      : filters.type.length === 1 && !filters.opal.length && !filters.category.length
      ? `Opal ${filters.type[0].toLowerCase()}`
      : filters.category.length === 1 && !filters.opal.length && !filters.type.length
      ? filters.category[0] === "Opal jewellery"
        ? "Opal jewellery"
        : filters.category[0]
      : filters.metal.length === 1 && !filters.opal.length && !filters.type.length && !filters.category.length
      ? `${filters.metal[0]} opal jewellery`
      : anyFilter
      ? "Filtered results"
      : "Shop the collection";

  const sortLabel = (SORTS.find(([k]) => k === sort) || SORTS[0])[1];
  const openGroup = groups.find((g) => g.key === menu);

  const optionRows = (g, big) =>
    g.options.map((o) => {
      const on = filters[g.key].includes(o.value);
      return (
        <button
          key={o.value}
          type="button"
          role="menuitemcheckbox"
          aria-checked={on}
          className={big ? "sc-sheet__opt" : "sc-panel__opt"}
          onClick={() => toggle(g.key, o.value)}
        >
          <CheckBox on={on} big={big} />
          <span className="sc-opt__label">{o.label}</span>
          <span className="sc-opt__count">{loading ? "" : o.count}</span>
        </button>
      );
    });

  return (
    <MarketingShell pageClass="pg-shop pg-shop-catalogue">
      <SEOHead
        title={`${title === "Shop the collection" ? "Shop Opal Jewellery & Souvenirs" : title} | Umoona Opal Mine Store`}
        description="Browse genuine Australian opal pendants and earrings — solid, doublet and triplet opal in sterling silver and gold plate — plus Coober Pedy souvenirs. Shipped Australia-wide."
        canonical="/online-store/shop"
      />

      {maintenance ? (
        <StoreMaintenance />
      ) : (
        <>
          {/* ============ HEADER ============ */}
          <section className="sc-head">
            <div className="sh-wrap">
              <nav className="sh-crumbs sc-head__crumbs" aria-label="Breadcrumb">
                <Link to="/online-store">Opal Store</Link>
                <span>/</span>
                <span className="sh-crumbs__here">Shop</span>
              </nav>
              <div className="sc-head__row">
                <div>
                  <h1 className="sc-head__title">{title}</h1>
                  <div className="sc-head__count">
                    {loading ? "Loading…" : `${results.length} ${results.length === 1 ? "piece" : "pieces"}`}
                  </div>
                </div>
                <div className="sc-search">
                  <Search size={17} className="sc-search__icon" />
                  <input
                    type="search"
                    value={search}
                    onChange={(e) => update({ search: e.target.value })}
                    placeholder="Search the collection"
                    aria-label="Search the collection"
                  />
                  {search && (
                    <button type="button" className="sc-search__clear" aria-label="Clear search" onClick={() => update({ search: "" })}>
                      <X size={12} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </section>

          {/* ============ FILTER BAR ============ */}
          <div className="sc-bar">
            <div className="sh-wrap sc-bar__inner">
              <div className="sc-bar__groups">
                {groups.map((g) => {
                  const n = filters[g.key].length;
                  const open = menu === g.key;
                  if (!g.options.length) return null;
                  return (
                    <div key={g.key} className="sc-group">
                      <button
                        type="button"
                        className={"sc-pill" + (n ? " has-sel" : "") + (open ? " is-open" : "")}
                        aria-haspopup="true"
                        aria-expanded={open}
                        onClick={() => setMenu(open ? null : g.key)}
                      >
                        {g.label}
                        {n > 0 && <span className="sc-pill__count">{n}</span>}
                        <ChevronDown size={14} className="sc-pill__caret" />
                      </button>
                      {open && !mobile && (
                        <div className="sc-panel" role="menu">
                          {optionRows(g, false)}
                          <div className="sc-panel__foot">
                            <button type="button" className="sc-panel__clear" onClick={() => update({ [g.key]: "" })}>
                              Clear
                            </button>
                            <button type="button" className="sc-panel__show" onClick={() => setMenu(null)}>
                              Show {results.length} results
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="sc-group sc-group--sort">
                <button
                  type="button"
                  className="sc-sortbtn"
                  aria-haspopup="true"
                  aria-expanded={menu === "sort"}
                  onClick={() => setMenu(menu === "sort" ? null : "sort")}
                >
                  <span className="sc-sortbtn__label">Sort:</span>
                  {sortLabel}
                  <ChevronDown size={14} />
                </button>
                {menu === "sort" && (
                  <div className="sc-panel sc-panel--sort" role="menu">
                    {SORTS.map(([key, label]) => (
                      <button
                        key={key}
                        type="button"
                        role="menuitemradio"
                        aria-checked={key === sort}
                        className={"sc-panel__sort" + (key === sort ? " is-on" : "")}
                        onClick={() => {
                          update({ sort: key === DEFAULT_SORT ? "" : key });
                          setMenu(null);
                        }}
                      >
                        {label}
                        {key === sort && <Check size={15} />}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          {menu && (
            <div
              className={"sc-scrim" + (mobile && openGroup ? " is-dim" : "")}
              onClick={() => setMenu(null)}
              aria-hidden="true"
            />
          )}

          {/* ============ RESULTS ============ */}
          <section className="sc-results">
            <div className="sh-wrap">
              {chips.length > 0 && (
                <div className="sc-chips">
                  {chips.map((c) => (
                    <span key={c.key} className="sc-chip">
                      {c.label}
                      <button type="button" aria-label={`Remove ${c.label}`} onClick={c.remove}>
                        <X size={11} />
                      </button>
                    </span>
                  ))}
                  <button type="button" className="sc-chips__clear" onClick={clearAll}>
                    Clear all
                  </button>
                </div>
              )}

              {error && (
                <div className="sh-error" role="alert">
                  <AlertCircle size={17} />
                  {error}
                </div>
              )}

              {loading ? (
                <SkeletonGrid count={8} className="sc-grid" />
              ) : results.length === 0 && !error ? (
                <div className="sc-empty">
                  <h3>No pieces match those filters</h3>
                  <p>Try removing a filter — or call us, we have more in the showroom than online.</p>
                  <button type="button" className="sh-btn sh-btn--ink" onClick={clearAll}>
                    Clear all filters
                  </button>
                </div>
              ) : (
                <>
                  <div className="sc-grid">
                    {results.slice(0, shown).map((p) => (
                      <ShopProductCard key={p._id || p.id} product={p} />
                    ))}
                  </div>
                  {results.length > shown && (
                    <div className="sc-more">
                      <button type="button" className="sh-btn sh-btn--outline" onClick={() => setShown((n) => n + PAGE_SIZE)}>
                        Load more
                      </button>
                      <span className="sc-more__count">
                        Showing {shown} of {results.length}
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
          </section>

          {/* ============ MOBILE BOTTOM SHEET ============ */}
          {mobile && openGroup && (
            <div className="sc-sheet" role="dialog" aria-label={openGroup.label}>
              <div className="sc-sheet__grab" />
              <div className="sc-sheet__head">
                <span>{openGroup.label}</span>
                <button type="button" aria-label="Close" onClick={() => setMenu(null)}>
                  <X size={16} />
                </button>
              </div>
              {optionRows(openGroup, true)}
              <div className="sc-sheet__foot">
                <button type="button" className="sc-sheet__clear" onClick={() => update({ [openGroup.key]: "" })}>
                  Clear
                </button>
                <button type="button" className="sc-sheet__show" onClick={() => setMenu(null)}>
                  Show {results.length} results
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </MarketingShell>
  );
}
